
import React, { useState } from 'react';

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  icon: string;
  faqs: FAQ[];
}

const FAQSections: React.FC = () => {
  const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});

  const toggleItem = (sectionIndex: number, faqIndex: number) => {
    const key = `${sectionIndex}-${faqIndex}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const faqSections: FAQSection[] = [
    {
      title: 'General Questions',
      icon: 'ri-question-line',
      faqs: [
        {
          question: 'What is Sylman Academy and what services do you offer?',
          answer: 'Sylman Academy is a leading training provider specialising in skills development, SETA compliance, and corporate training solutions. We offer learnership programmes, skills programmes, occupational certificates, and B-BBEE support services to help organisations develop their workforce and meet compliance requirements.'
        },
        {
          question: 'Are you accredited and what are your credentials?',
          answer: 'Yes, we are fully accredited by the Quality Council for Trades and Occupations (QCTO) and registered with multiple SETAs. Our trainers are qualified professionals with industry experience and relevant certifications. We maintain all necessary accreditations to deliver quality training programmes.'
        },
        {
          question: 'What industries do you serve?',
          answer: 'We serve a wide range of industries including retail, manufacturing, agriculture, hospitality, healthcare, construction, mining, finance, and technology. Our programmes are designed to meet the specific needs of different sectors and can be customised for your industry requirements.'
        },
        {
          question: 'Where are you located and do you offer training nationwide?',
          answer: 'Our main facility is located in Midrand, Gauteng, at No 6 Riboville Road, 183 Randjesfontein Country Estate. We offer training both at our facilities and on-site at client locations. We provide services nationwide across South Africa and can arrange training delivery in any province.'
        },
        {
          question: 'How do I get started with Sylman Academy?',
          answer: 'Getting started is easy! Contact us for a free consultation where we\'ll assess your training needs, discuss available programmes, and provide a customised proposal. You can reach us by phone, email, or through our contact form on the website.'
        }
      ]
    },
    {
      title: 'SETA & Funding Questions',
      icon: 'ri-government-line',
      faqs: [
        {
          question: 'What is SETA and why is compliance important?',
          answer: 'SETA (Sector Education and Training Authority) is a government body that oversees skills development in specific economic sectors. Compliance is mandatory for companies with annual payrolls over R500,000. It involves paying Skills Development Levy (SDL) and submitting Workplace Skills Plans (WSP) and Annual Training Reports (ATR).'
        },
        {
          question: 'What are the key SETA compliance deadlines I need to know?',
          answer: 'Key deadlines include: April 30th for both WSP and ATR submission, and ongoing deadlines for discretionary grant applications. Missing these deadlines can result in penalties and loss of funding opportunities. We help ensure you never miss critical dates.'
        },
        {
          question: 'How much SETA funding can my company access?',
          answer: 'Companies can access up to 70% of their SDL contributions through mandatory and discretionary grants. This includes 20% mandatory grants for compliant companies and up to 50% discretionary grants for approved training programmes. The exact amount depends on your payroll and compliance status.'
        },
        {
          question: 'What is the difference between mandatory and discretionary grants?',
          answer: 'Mandatory grants (20% of SDL) are automatically available to compliant companies for any accredited training. Discretionary grants (up to 50% of SDL) require application approval and must align with SETA priorities and sector skills plans. We help you access both types of funding.'
        },
        {
          question: 'Can you help with SETA submissions and compliance?',
          answer: 'Absolutely! We provide comprehensive SETA compliance support including WSP and ATR preparation, submission assistance, grant applications, and ongoing compliance monitoring. Our experts ensure your submissions are accurate, complete, and submitted on time.'
        }
      ]
    },
    {
      title: 'Programme & Delivery Questions',
      icon: 'ri-book-open-line',
      faqs: [
        {
          question: 'What types of training programmes do you offer?',
          answer: 'We offer learnership programmes (12-24 months), skills programmes (3-12 months), occupational certificates, and short courses. Programmes cover areas like business administration, customer service, retail operations, agriculture, and more. All programmes are accredited and lead to recognised qualifications.'
        },
        {
          question: 'How long do your training programmes take to complete?',
          answer: 'Programme duration varies: Skills programmes typically take 3-12 months, learnerships run 12-24 months, and short courses can be 1-5 days. Duration depends on the qualification level, delivery method, and whether training is full-time or part-time. We provide flexible scheduling options.'
        },
        {
          question: 'Do you offer online or remote training options?',
          answer: 'Yes, we offer blended learning solutions combining online theory with practical sessions. Some programmes can be delivered entirely online, while others require hands-on practical components. We use modern learning management systems and virtual classroom technologies for effective remote delivery.'
        },
        {
          question: 'What qualifications will learners receive upon completion?',
          answer: 'Learners receive nationally recognised qualifications registered on the National Qualifications Framework (NQF). These include certificates, diplomas, or statements of results depending on the programme. All qualifications are issued by accredited bodies and recognised by employers nationwide.'
        },
        {
          question: 'Can training be customised for our specific business needs?',
          answer: 'Absolutely! We specialise in customising training content to match your business processes, industry requirements, and company culture. Our team works with you to develop bespoke programmes that address your specific skills gaps and operational needs while maintaining accreditation standards.'
        },
        {
          question: 'What support do you provide during and after training?',
          answer: 'We provide comprehensive support including pre-training assessments, ongoing mentoring, progress monitoring, and post-training follow-up. Learners have access to learning materials, online resources, and trainer support throughout their journey. We also offer refresher training and continuous development opportunities.'
        }
      ]
    },
    {
      title: 'Payment & Administration Questions',
      icon: 'ri-money-dollar-circle-line',
      faqs: [
        {
          question: 'What are your training fees and payment options?',
          answer: 'Training fees vary by programme type, duration, and delivery method. We offer competitive rates and flexible payment options including upfront payment, installments, and SETA grant funding. Contact us for a detailed quote based on your specific requirements and group size.'
        },
        {
          question: 'Can SETA grants cover the full cost of training?',
          answer: 'SETA grants can cover a significant portion of training costs, often 70-100% depending on the programme and your company\'s grant allocation. We help maximise your grant utilisation and can arrange training within your available grant funding. Any shortfall can be covered through flexible payment arrangements.'
        },
        {
          question: 'What happens if learners don\'t complete the programme?',
          answer: 'We have high completion rates due to our support systems, but if a learner withdraws, we work with you to find solutions. This may include transferring to a different programme, extending the completion period, or arranging catch-up sessions. SETA funding implications are managed according to grant conditions.'
        },
        {
          question: 'Do you provide certificates and how long does it take?',
          answer: 'Yes, we provide all certificates and qualifications upon successful completion. Internal certificates are issued within 2-4 weeks, while external body certifications (like QCTO) may take 3-6 months. We provide interim statements of results to confirm completion while waiting for official certificates.'
        }
      ]
    },
    {
      title: 'B-BBEE Questions',
      icon: 'ri-team-line',
      faqs: [
        {
          question: 'How does training impact our B-BBEE scorecard?',
          answer: 'Training contributes significantly to your B-BBEE Skills Development scorecard element, worth 20 points. Spending on training, particularly for black employees and disabled learners, earns points. Learnerships and internships provide additional points. We help maximise your B-BBEE benefits through strategic training planning.'
        },
        {
          question: 'What training expenditure counts towards B-BBEE points?',
          answer: 'B-BBEE recognises training expenditure on black employees, disabled learners, and unemployed learners. This includes programme fees, learner stipends, and related costs. Learnerships and skills programmes for unemployed black learners earn the highest points. We ensure all training is structured to maximise B-BBEE benefits.'
        }
      ]
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-12">
          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-green-600 text-white px-6 py-4">
                <h2 className="text-2xl font-bold flex items-center">
                  <i className={`${section.icon} mr-3 text-2xl`}></i>
                  {section.title}
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200">
                {section.faqs.map((faq, faqIndex) => {
                  const key = `${sectionIndex}-${faqIndex}`;
                  const isOpen = openItems[key];
                  
                  return (
                    <div key={faqIndex} className="border-b border-gray-200 last:border-b-0">
                      <button
                        onClick={() => toggleItem(sectionIndex, faqIndex)}
                        className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
                      >
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900 pr-4">
                            {faq.question}
                          </h3>
                          <div className="flex-shrink-0">
                            <i className={`ri-${isOpen ? 'subtract' : 'add'}-line text-green-600 text-xl`}></i>
                          </div>
                        </div>
                      </button>
                      
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p className="text-gray-700 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSections;
